import React, { useState, useEffect } from 'react';
import { Form, Input, Select } from 'grab-ui';
import { Alert, Popover, Flex } from '@duxton-web/ui';
import { HelpOutlined } from '@duxton-web/icons';
import {
  VERTICALS_DISPLAY_NAMES,
  BUDGET_SOURCE_DISPLAY_NAMES,
  CHANNEL_GROUP_DISPLAY_NAMES, CHANNEL_GROUP_CHANNELS, CHANNEL_DISPLAY_NAMES, CHANNEL_GROUPS, VERTICALS_TO_IGNORED,
} from 'omega-rtc/constants/ucd';
import { generateCampaignCode, getTimezoneNameFromAbbrv } from 'omega-rtc/utils/ucd-utils';
import _ from 'lodash';
import UCDCampaignCodeFormItem, { CAMPAIGN_CODE_MODES } from 'omega-rtc/components/form/ucd/common/UCDCampaignCodeFormItem';
import DatePickerWrapper from 'omega-rtc/components/form/ucd/common/DatePickerWrapper';
import moment from 'moment-timezone';
import pmIcon from 'img/ucd_channelgroup_pm.svg';
import hedwigIcon from 'img/ucd_channelgroup_hedwig.svg';
import tridentIcon from 'img/ucd_channelgroup_trident.svg';
import feedIcon from 'img/ucd_channelgroup_feed.svg';
import salesforceIcon from 'img/ucd_channelgroup_salesforce.svg';
import 'omega-rtc/components/form/ucd/AddCampaignDetailForm.scss';
import { getTimezoneOptions } from 'omega-rtc/utils/time-utils';


type Props = {
  channelGroup: string,
  initialValues: any,
  hideOptions: Array<string>,
  selectProps: any,

  form: any,
  configs: any,
  countries: Object,
  router: any,
};

const channelGroupIconMapping = {
  [CHANNEL_GROUPS.pm]: pmIcon,
  [CHANNEL_GROUPS.hedwig]: hedwigIcon,
  [CHANNEL_GROUPS.trident]: tridentIcon,
  [CHANNEL_GROUPS.feed]: feedIcon,
  [CHANNEL_GROUPS.salesforce]: salesforceIcon,
};

const AddCampaignDetailsForm = (props: Props) => {
  // Channel Group related fields
  const { hideOptions, selectProps, countries } = props;
  const showLabel = !hideOptions || !hideOptions.includes('channelGroupTitle');
  const showChannelType = !hideOptions || !hideOptions.includes('channelType');
  const showChannelGroup =
    !hideOptions || !hideOptions.includes('channelGroup');


  const [campaignCodeGenerationMode, setCampaignCodeGenerationMode] = useState(CAMPAIGN_CODE_MODES.auto);
  const [autoGeneratedCampaignCode, setAutoGeneratedCampaignCode] = useState('');
  const { getFieldDecorator, setFieldsValue } = props.form;
  const countryID = _.get(props.form.getFieldsValue(), 'project.project.countryID', {});
  const timezones = _.get(_.find(countries, { countryID }, {}), 'timezones', []);


  const shouldShowCampaignCode = () => {
    const { campaignDescription, campaignName, startDate, endDate, budgetType, vertical } = props.form.getFieldsValue();
    return !!(campaignDescription && campaignName && startDate && endDate && budgetType && vertical);
  };

  const getCampaignCodeDependencies = () => {
    const { budgetType, vertical, project, startDate, campaignName } = props.form.getFieldsValue();
    const { audience, objective, countryID } = _.get(project, 'project', {});
    return `${budgetType}-${vertical}-${audience}-${objective}-${campaignName}-${countryID}-${startDate}`;
  };

  const onTimezoneChange = (form, timezone, countryTimezones) => {
    const currentStartDate = moment(form.getFieldValue('startDate'));
    const currentEndDate = moment(form.getFieldValue('endDate'));
    const timezoneName = getTimezoneNameFromAbbrv(timezone, countryTimezones);
    form.setFieldsValue({
      startDate: currentStartDate.tz(timezoneName),
      endDate: currentEndDate.tz(timezoneName),
    });
  };

  useEffect(() => {
    if (shouldShowCampaignCode()) {
      const code = generateCampaignCodeHelper();
      setAutoGeneratedCampaignCode(code);
    } else if (props.form.getFieldValue('campaignCode')) {
      props.form.setFieldsValue({ campaignCode: '' });
    }
  }, [getCampaignCodeDependencies(), shouldShowCampaignCode()]);

  const generateCampaignCodeHelper = () => {
    const { budgetType, vertical, project, startDate, campaignName } = props.form.getFieldsValue();
    const { audience, objective, countryID } = _.get(project, 'project', {});
    return generateCampaignCode({
      audience,
      vertical,
      objective,
      shortName: campaignName,
      budgetType,
      countryCode: _.find(props.countries, ['countryID', countryID]).code,
      year: startDate.format('YYYY'),
    });
  };

  const filteredVerticals = _.filter(
    props.configs.verticals,
    (vertical) => !VERTICALS_TO_IGNORED.includes(vertical),
  );
  const filteredVerticalOptions = _.map(filteredVerticals, (vertical) => (
    <Select.Option key={vertical} value={vertical}>
      {_.get(VERTICALS_DISPLAY_NAMES, vertical)}
    </Select.Option>
  ));

  const showAlert = props.form.getFieldValue('showCampaignCodeExistsAlert');
  const getCampaignCodeExistsError = () => {
    if (campaignCodeGenerationMode === CAMPAIGN_CODE_MODES.manual) {
      return 'Campaign code already exists. Please change the campaign code';
    } else if (campaignCodeGenerationMode === CAMPAIGN_CODE_MODES.auto) {
      return 'Campaign code already exists. Please change the campaign name';
    }
  };
  return (
    <div style={{ marginTop: '5px', marginBottom: '36px' }}>
      <div>
        <Form.Item>
          {
            getFieldDecorator('showCampaignCodeExistsAlert', { initialValue: false })(
              showAlert ? <Alert showIcon closable message={getCampaignCodeExistsError()} type="error" /> : <div />
            )
          }
        </Form.Item>
      </div>

      <div>
        {showChannelGroup && (
          <div>
            {showLabel && (
              <div style={{ fontWeight: 'bold' }}>Selected Channel Group</div>
            )}
            <div className={'selected-channel-group-panel'}>
              <img
                src={_.get(channelGroupIconMapping, props.channelGroup)}
                alt={props.channelGroup}
              />
              <div className={'title'}>
                {_.get(CHANNEL_GROUP_DISPLAY_NAMES, props.channelGroup)}
              </div>
            </div>
          </div>
        )}
        {showChannelType && (
          <div style={{ marginTop: '24px' }}>
            <Form.Item>
              <div style={{ marginBottom: '11px', fontWeight: 'bold' }}>
                <span style={{ color: 'red', marginRight: '3px' }}>*</span>Channel
                Type
              </div>
              {props.form.getFieldDecorator('channelType', {
                rules: [
                  {
                    required: 'true',
                  },
                ],
                initialValue: _.get(props, 'initialValues.channelType'),
              })(
                <Select
                  mode={'multiple'}
                  placeholder={'Select Channel Type'}
                  {...(selectProps || {})}
                >
                  {_.get(CHANNEL_GROUP_CHANNELS, props.channelGroup, []).map(
                    channel => (
                      <Select.Option value={channel} key={channel}>
                        {_.get(CHANNEL_DISPLAY_NAMES, channel)}
                      </Select.Option>
                    ),
                  )}
                </Select>,
              )}
            </Form.Item>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div style={{ flexBasis: '48%' }}>
          <div style={{ marginBottom: '11px', fontWeight: 'bold' }}><span style={{ color: 'red', marginRight: '3px' }}>*</span>Campaign Description</div>
          <Form.Item>
            {getFieldDecorator('campaignDescription', {
              rules: [
                {
                  required: true,
                },
              ],
            })(
              <Input placeholder={'Enter Campaign Name'} />
            )}
          </Form.Item>
        </div>
        <div style={{ flexBasis: '48%' }}>
          <Flex style={{ marginBottom: '11px', fontWeight: 'bold' }} gap={4}>
            <span style={{ color: 'red', marginRight: '3px' }}>*</span>
            Campaign Name
            <Popover
              title="Campaign Name"
              content={'Campaign name must be unique alphanumeric text that is less than 10 characters.'}
              trigger="hover"
            >
              <HelpOutlined style={{ fontSize: '20px' }} />
            </Popover>
          </Flex>
          <Form.Item>
            {getFieldDecorator('campaignName', {
              rules: [
                {
                  required: true,
                  message:
                    'Please select unique alphanumeric text that is less than 10 characters',
                  pattern: '^[a-zA-Z0-9]{1,10}$',
                },
              ],
            })(
              <Input placeholder={'placeholder="Ex.ONEGRABPRO"'} />
            )}
          </Form.Item>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div style={{ flexBasis: '48%' }}>
          <div style={{ marginBottom: '11px', fontWeight: 'bold' }}><span style={{ color: 'red', marginRight: '3px' }}>*</span>Start Date</div>
          <Form.Item>
            {getFieldDecorator('startDate', {
              rules: [
                {
                  required: true,
                },
              ],
            })(
              <DatePickerWrapper
                showTime={{ format: 'HH:mm' }}
                format={'YYYY-MM-DD HH:mm'}
                disabledDate={current => moment(current).isBefore(moment().startOf('day'))}
                defaultPickerValue={moment()}
                style={{ width: '100%' }}
                placeholder={'Select Start Date'}
                allowClear
              />
            )}
          </Form.Item>
        </div>
        <div style={{ flexBasis: '48%' }}>
          <div style={{ marginBottom: '11px', fontWeight: 'bold' }}><span style={{ color: 'red', marginRight: '3px' }}>*</span>End Date</div>
          <Form.Item>
            {getFieldDecorator('endDate', {
              rules: [
                {
                  required: true,
                },
              ],
            })(
              <DatePickerWrapper
                showTime={{ format: 'HH:mm' }}
                format={'YYYY-MM-DD HH:mm'}
                disabledDate={
                  // Disable date if it is before start date
                  current => moment(current).isBefore(moment().startOf('day')) && current.isAfter(props.form.getFieldValue('startDate'))

                }
                defaultPickerValue={moment().add(14, 'days')}
                style={{ width: '100%' }} placeholder={'Select End Date'} allowClear
              />
            )}
          </Form.Item>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div style={{ flexBasis: '48%' }}>
          <div className={'timezone-field'} style={{ marginBottom: '11px', fontWeight: 'bold' }}><span style={{ color: 'red', marginRight: '3px' }}>*</span>Timezone</div>
          <Form.Item>
            {getFieldDecorator('timezone', {
              rules: [
                {
                  required: true,
                },
              ],
            })(
              <Select
                placeholder={'Select Timezone'} onChange={(value) => {
                  onTimezoneChange(props.form, value, timezones);
                }}
              >
                {_.map(getTimezoneOptions(timezones), timezone => (
                  <Select.Option key={timezone.value} value={timezone.value} >
                    {timezone.text}
                  </Select.Option>
                ))}
              </Select>
            )}
          </Form.Item>
        </div>
        <div style={{ flexBasis: '48%' }}>
          <div style={{ marginBottom: '11px', fontWeight: 'bold' }}><span style={{ color: 'red', marginRight: '3px' }}>*</span>Budget Type</div>
          <Form.Item>
            {getFieldDecorator('budgetType', {
              rules: [
                {
                  required: true,
                },
              ],
            })(
              <Select placeholder={'Select Budget Type'} >
                {_.map(props.configs.budgetSources, budgetSource => (
                  <Select.Option key={budgetSource} value={budgetSource}>
                    {_.get(BUDGET_SOURCE_DISPLAY_NAMES, budgetSource, budgetSource)}
                  </Select.Option>
                ))}
              </Select>
            )}
          </Form.Item>
        </div>

      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div style={{ flexBasis: '48%' }}>
          <div style={{ marginBottom: '11px', fontWeight: 'bold' }}><span style={{ color: 'red', marginRight: '3px' }}>*</span>Vertical</div>
          <Form.Item>
            {getFieldDecorator('vertical', {
              rules: [
                {
                  required: true,
                },
              ],
            })(
              <Select placeholder={'Select Vertical'}>
                {filteredVerticalOptions}
              </Select>
            )}
          </Form.Item>
        </div>
      </div>

      {shouldShowCampaignCode() && (
        <div>
          <div style={{ marginBottom: '11px', fontWeight: 'bold' }}>
            <span style={{ color: 'red', marginRight: '3px' }}>*</span>Campaign
            Code
          </div>
          <Form.Item>
            {
              getFieldDecorator('campaignCode', {
                rules: [
                  {
                    required: true,
                    message:
                      'Campaign code cannot be empty',
                  },
                ],
              })(
                <UCDCampaignCodeFormItem
                  initialMode={CAMPAIGN_CODE_MODES.auto}
                  initialCode={autoGeneratedCampaignCode}
                  autoGeneratedCode={autoGeneratedCampaignCode}
                  onCodeChange={code => setFieldsValue({ campaignCode: code })}
                  onModeChange={mode => {
                    setCampaignCodeGenerationMode(mode);
                    props.form.setFieldsValue({ showCampaignCodeExistsAlert: false });
                  }}
                />
              )
            }
          </Form.Item>

        </div>
      )}
    </div>
  );
};

export default AddCampaignDetailsForm;
